import Dexie, { Table } from 'dexie';
import Cookies from "js-cookie";

/**
 * ─────────────────────────────────────────────────────────────────────────
 * MASTER SWITCH for the local Dexie / IndexedDB cache.
 *
 *   true  → normal behavior: API responses are cached in IndexedDB and the UI
 *           reads from that cache via live queries.
 *   false → Dexie is bypassed EVERYWHERE. No reads, writes, opens, or resets
 *           touch IndexedDB; the whole app runs straight off API responses
 *           (the same code path used when a browser has no IndexedDB support).
 *
 * Flip this constant on/off at any time — nothing else needs to change.
 * ─────────────────────────────────────────────────────────────────────────
 */
export const USE_DEXIE = false;

/**
 * True only when the cache is both switched on (USE_DEXIE) AND actually
 * available in this environment. Every cache read/write/open gate uses this
 * instead of touching `window.indexedDB` directly, so the switch above
 * disables the cache consistently across the whole app.
 */
export const isDexieEnabled = (): boolean =>
    USE_DEXIE && typeof window !== "undefined" && !!window.indexedDB;

// Define the data types for the tables
interface AnalyticsData {
    id?: number;
    "24h_valuation": {
        value: string;
        percentage: string;
    };
    "7d_valuation": {
        value: string;
        percentage: string;
    };
}

interface Asset {
    name: string;
    free: number;
    locked: number;
    weight: number;
    value: number;
    pnl: {
        value: number | null;
        percentage: number | null;
        all_time_value: number | null;
        all_time_percentage: number | null;
    };
    provider_name: string | null;
    provider_id: number | null;
    provider_ids: any[];
    avg_buy_value: number;
    avg_sell_value: number;
    current_price: number;
    open_orders: {
        sell: number;
        buy: number;
    };
    open_orders_count: {
        sell: number;
        buy: number;
    };
    asset_details: {
        id: string;
        name: string;
        image: string;
        symbol: string;
        market_cap: number;
        current_price: number;
        market_cap_rank: number;
        price_change_24h: number;
        price_change_percentage_24h: number;
        price_change_percentage_1h_in_currency: number;
        price_change_percentage_7d_in_currency: number;
        price_change_percentage_14d_in_currency: number;
        price_change_percentage_24h_in_currency: number;
    };
    account_type: string;
}

interface Holdings {
    id: number;
    provider: number;
    name: string;
    assets: Asset[];
    portfolio_pnl: {
        value: number | null;
        percentage: number | null;
        all_time_value: number | null;
        all_time_percentage: number | null;
    };
    evaluation: number;
    trade_sync_status: string;
    last_orders_synch_date: string;
    exchange_key_id: number
}

interface ExchangeKey {
    id?: number;
    exchange_key_id?: number;
    name: string;
    provider_id: number;
    provider_name: string;
    '24h_change_value': number;
    '24h_change_percentage': number;
    last_5m_evaluation: number;
    last_24h_evaluation: number;
    creation_date: string;
    public_key: string;
    key_status_name: string;
    provider_symbol_seperator: string;
    account_type: string;
}

interface Snapshots {
    id?: number;
    range: string;
    exchange_key_id?: number;
    data: SnapshotsData;
}

interface SnapshotsData {
    created_at: string;
    total_evaluation: number;
}

const DB_NAME = 'AryaCrypto';
// Bumped 14 -> 15 to force a one-time reset of every existing user's local
// Dexie DB on their next load (see checkVersion): heals users already carrying
// stale holdings that render portfolio values as 0.
const DB_VERSION = 15;

class AryaCryptoDB extends Dexie {
    analytics!: Table<AnalyticsData, number>;
    holdings!: Table<Holdings, number>;
    exchangeKeys!: Table<ExchangeKey, number>;
    snapshots!: Table<Snapshots, number>;
    selectedExchange!: Table<ExchangeKey, number>

    constructor() {
        super(DB_NAME);

        this.version(DB_VERSION).stores({
            analytics: '++id',
            holdings: 'id',
            exchangeKeys: '++id',
            snapshots: '++id, [range+exchange_key_id]',
            selectedExchange: "++id"
        });
    }

    async init() {
        if (!isDexieEnabled()) {
            return; // Skip when disabled (SSR, no IndexedDB, or USE_DEXIE=false)
        }

        try {
            await this.checkVersion();
        } catch (error) {
            console.error('Error during database initialization:', error);
        }
    }

    private async checkVersion() {
        if (!isDexieEnabled()) {
            return; // Skip when disabled (SSR, no IndexedDB, or USE_DEXIE=false)
        }

        const currentVersion = Cookies.get('aryaCryptoVersion');
        if (currentVersion !== DB_VERSION.toString()) {
            try {
                await this.delete();
                Cookies.set('aryaCryptoVersion', DB_VERSION.toString());
                console.log('Database reset due to version change');
                await this.open();
            } catch (error) {
                console.error('Error resetting database:', error);
                throw error;
            }
        }
    }

    async resetDatabase(): Promise<void> {
        if (!isDexieEnabled()) {
            return; // Nothing to reset when disabled (SSR / no IndexedDB / USE_DEXIE=false)
        }

        try {
            await this.delete();
            Cookies.set('aryaCryptoVersion', DB_VERSION.toString());
            await this.open();
        } catch (error) {
            console.error('Error resetting database:', error);
            throw error;
        }
    }
}

// Create database instance with deferred initialization
const db = new AryaCryptoDB();

// Initialize the database when in browser context and the cache is enabled
if (isDexieEnabled()) {
    db.init().catch(console.error);
}

// CRUD Functions
export async function addAnalyticsData(analytics: AnalyticsData) {
    if (!isDexieEnabled()) return;

    try {
        await db.transaction('rw', db.analytics, async () => {
            await db.analytics.clear();
            await db.analytics.add(analytics);
        });
    } catch (error) {
        console.error('Error adding analytics data:', error);
        throw error;
    }
}


export async function addSelectedExchangeData(data: ExchangeKey) {
    if (!isDexieEnabled()) return;

    if (!data) {
        return;
    }

    // Ensure required fields are present
    const validatedData = {
        ...data,
        // Remove undefined id to let Dexie auto-generate it
        id: undefined
    };

    try {
        await db.transaction('rw', db.selectedExchange, async () => {
            await db.selectedExchange.clear();
            await db.selectedExchange.add(validatedData);
        });
    } catch (error) {
        console.error('Error adding selected exchange data:', error);
        throw error;
    }
}

export async function addHoldingsData(data: Holdings | Holdings[]) {
    if (!isDexieEnabled()) return;

    // The aggregate ("All") portfolio has no exchange_key_id — normalize it to
    // 0 so it is stored with a valid, stable key that the read side looks up as
    // `exchange_key_id ?? 0`. (null/undefined are not valid IndexedDB keys.)
    const normalizeId = (e: Holdings) => ({ ...e, id: e.exchange_key_id ?? 0 });

    try {
        if (Array.isArray(data)) {
            // A full list is authoritative: upsert everything in it and drop any
            // stale rows that are no longer present (e.g. disconnected exchanges).
            // Without this reconcile, stale rows accumulate and break the
            // focused-portfolio lookup, showing 0 values.
            const holdings = data.map(normalizeId);
            const incomingIds = new Set(holdings.map(item => item.id));

            await db.transaction('rw', db.holdings, async () => {
                const existingIds = await db.holdings.toCollection().primaryKeys();
                const idsToDelete = existingIds.filter(id => !incomingIds.has(id as number));
                if (idsToDelete.length > 0) {
                    await db.holdings.bulkDelete(idsToDelete);
                }
                await db.holdings.bulkPut(holdings);
            });
        } else {
            // A single-exchange fetch only owns its own row — upsert just that one.
            await db.holdings.put(normalizeId(data));
        }
    } catch (error) {
        console.error('Error managing holdings data:', error);
        throw error;
    }
}

export async function addConnectedKeys(keys: ExchangeKey[]) {
    if (!isDexieEnabled()) return;

    try {
        // Get all existing keys from the database
        const existingKeys = await db.exchangeKeys.toArray();

        // Separate new and existing keys using a more specific identifier
        const keysToAdd: ExchangeKey[] = [];
        const keysToUpdate: ExchangeKey[] = [];
        const incomingKeysSet = new Set(keys.map(key => `${key.exchange_key_id}-${key.provider_id}`));

        keys.forEach(key => {
            const existingKey = existingKeys.find(
                existing => existing.exchange_key_id === key.exchange_key_id
            );

            if (existingKey) {
                // If key exists, prepare for update
                keysToUpdate.push({ ...key, id: existingKey.id });
            } else {
                // If key doesn't exist, prepare for add
                keysToAdd.push(key);
            }
        });

        // Identify keys to delete (those in the database but not in the incoming keys array)
        const keysToDelete = existingKeys.filter(existingKey => {
            const keyIdentifier = `${existingKey.exchange_key_id}-${existingKey.provider_id}`;
            return !incomingKeysSet.has(keyIdentifier);
        });

        // Add new keys
        if (keysToAdd.length > 0) {
            await db.exchangeKeys.bulkAdd(keysToAdd);
        }

        // Update existing keys
        if (keysToUpdate.length > 0) {
            await db.exchangeKeys.bulkPut(keysToUpdate);
        }

        // Delete remaining keys
        if (keysToDelete.length > 0) {
            const idsToDelete = keysToDelete.map(key => key.id) as number[];
            await db.exchangeKeys.bulkDelete(idsToDelete);
        }
    } catch (error) {
        console.error('Error managing exchange keys:', error);
        throw error;
    }
}

export async function addSnapshotsData(
    data: SnapshotsData,
    range = '1week',
    exchange_key_id: number | null
) {
    if (!isDexieEnabled()) return;

    try {
        const evaluationEntry: Snapshots = {
            range: range,
            exchange_key_id: exchange_key_id ?? 0,
            data: data,
        };

        const existingEntry = await db.snapshots
            .where('[range+exchange_key_id]')
            .equals([range, (exchange_key_id ?? 0)])
            .first();

        if (existingEntry) {
            await db.snapshots.update(existingEntry.id!, JSON.parse(JSON.stringify(evaluationEntry)));
        } else {
            await db.snapshots.add(JSON.parse(JSON.stringify(evaluationEntry)));
        }
    } catch (error) {
        console.error('Error saving snapshot data:', error);
        throw error;
    }
}

export {
    db,
    type AnalyticsData,
    type Asset,
    type Holdings,
    type ExchangeKey,
    type Snapshots,
    type SnapshotsData
};